/**
 * Global top-of-page banner that appears whenever an admin is
 * impersonating another user. Reads from localStorage + AuthContext.
 *
 * Offers a single "Stop" CTA that restores the original admin access_token
 * and reloads. Server-side audit logging happens in /admin-users/stop-impersonation.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Eye, X } from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../contexts/AuthContext";

export function ImpersonationBanner() {
  const { user } = useAuth();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    // Two signals: server-stamped flag OR local storage marker (covers
    // the brief window before AuthContext refreshes).
    const fromServer = user?._impersonated ? user.email : null;
    const fromLocal = localStorage.getItem("impersonation_target");
    const hasRestore = !!localStorage.getItem("admin_restore_token");
    if ((fromServer || fromLocal) && hasRestore) {
      setTarget(fromServer || fromLocal);
    } else {
      setTarget(null);
    }
  }, [user]);

  if (!target) return null;

  const stop = async () => {
    try { await axios.post(`${API}/admin-users/stop-impersonation`, {}, { withCredentials: true }); } catch {}
    const adminToken = localStorage.getItem("admin_restore_token");
    if (adminToken) {
      localStorage.setItem("access_token", adminToken);
      localStorage.removeItem("admin_restore_token");
    }
    localStorage.removeItem("impersonation_target");
    window.location.href = "/dashboard";
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[10001] bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 shadow-lg flex items-center justify-center gap-3 text-sm font-bold"
      role="alert"
      data-testid="impersonation-banner"
    >
      <Eye className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">
        Viewing as <span className="underline">{target}</span> · all actions are audit-logged
      </span>
      <button
        onClick={stop}
        className="ml-2 px-3 py-1 bg-black/25 hover:bg-black/40 rounded transition-colors flex items-center gap-1"
        data-testid="impersonation-stop-btn"
      >
        <X className="w-3 h-3" /> Stop
      </button>
    </div>
  );
}
